import config from "config/config";
import { authService } from "./authService";
import {
    CreateSpacePayload,
    UpdateSpacePayload,
    SpaceFilters,
    AddSpaceMembersPayload,
    AddSpaceGuestsPayload,
    SpaceGuestAcceptPayload,
    SpaceGuestLoginPayload,
    SpaceGuestTokenVerifyPayload,
    CreateSpaceTaskPayload,
    UpdateSpaceTaskPayload,
    SpaceTaskFilters,
} from "redux/Ldb/types/spaceTypes";
import { createUrlSearchParams } from "helpers";

export const spaceService = {
    getSingleSpace,
    getSpaces,
    createSpace,
    updateSpace,
    deleteSpace,
    pinSpace,
    unpinSpace,
    archiveSpace,
    unarchiveSpace,
    addSpaceMembers,
    removeSpaceMember,
    addSpaceGuests,
    removeSpaceGuest,
    spaceGuestAccept,
    spaceGuestLogin,
    spaceGuestTokenVerify,
    getSpaceGuestInvitePreview,
    getSpaceGuestBrands,
    createSpaceFile,
    getSpaceFiles,
    deleteSpaceFile,
    getSingleSpaceFile,
    createSpaceNote,
    updateSpaceNote,
    getSpaceNotes,
    getSpaceSingleNote,
    deleteSpaceNote,
    pinSpaceNote,
    unpinSpaceNote,
    leaveSpace,
    // Space Message Services
    getSpaceMessages,
    sendSpaceMessage,
    pinSpaceMessage,
    unpinSpaceMessage,
    editSpaceMessage,
    deleteSpaceMessage,
    // Space Task Services
    getSpaceTasks,
    getSingleSpaceTask,
    createSpaceTask,
    updateSpaceTask,
    deleteSpaceTask,
    getSpaceTasksRuns,
    fetchSpaceUnreadCount,
};

async function getSingleSpace(id: string) {
    try {
        const res = await authService.apiGate(`${config.API_URL}/api/v1/ldb/spaces/${id}`, {
            method: "GET",
        });
        return res;
    } catch (error) {
        console.error("Error fetching single space:", error);
        throw error;
    }
}

async function getSpaces(filters?: SpaceFilters) {
    try {
        const queryParams = createUrlSearchParams((filters as any) || {});

        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces?${queryParams.toString()}`,
            {
                method: "GET",
            }
        );
        return res;
    } catch (error) {
        console.error("Error fetching spaces:", error);
        throw error;
    }
}

async function createSpace(data: CreateSpacePayload) {
    try {
        const res = await authService.apiGate(`${config.API_URL}/api/v1/ldb/spaces`, {
            method: "POST",
            body: JSON.stringify(data),
        });
        return res;
    } catch (error) {
        console.error("Error creating space:", error);
        throw error;
    }
}

async function updateSpace(spaceId: string, data: UpdateSpacePayload) {
    try {
        const res = await authService.apiGate(`${config.API_URL}/api/v1/ldb/spaces/${spaceId}`, {
            method: "PUT",
            body: JSON.stringify(data),
        });
        return res;
    } catch (error) {
        console.error("Error updating space:", error);
        throw error;
    }
}

async function deleteSpace(id: string) {
    try {
        const res = await authService.apiGate(`${config.API_URL}/api/v1/ldb/spaces/${id}`, {
            method: "DELETE",
        });
        return res;
    } catch (error) {
        console.error("Error deleting space:", error);
        throw error;
    }
}

async function pinSpace(id: string) {
    try {
        const res = await authService.apiGate(`${config.API_URL}/api/v1/ldb/spaces/${id}/pin`, {
            method: "PUT",
        });
        return res;
    } catch (error) {
        console.error("Error pinning space:", error);
        throw error;
    }
}

async function unpinSpace(id: string) {
    try {
        const res = await authService.apiGate(`${config.API_URL}/api/v1/ldb/spaces/${id}/unpin`, {
            method: "PUT",
        });
        return res;
    } catch (error) {
        console.error("Error unpinning space:", error);
        throw error;
    }
}

async function archiveSpace(id: string) {
    try {
        const res = await authService.apiGate(`${config.API_URL}/api/v1/ldb/spaces/${id}/archive`, {
            method: "PUT",
        });
        return res;
    } catch (error) {
        console.error("Error archiving space:", error);
        throw error;
    }
}

async function unarchiveSpace(id: string) {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${id}/unarchive`,
            {
                method: "PUT",
            }
        );
        return res;
    } catch (error) {
        console.error("Error unarchiving space:", error);
        throw error;
    }
}

async function addSpaceMembers(spaceId: string, data: AddSpaceMembersPayload) {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${spaceId}/members`,
            {
                method: "PUT",
                body: JSON.stringify(data),
            }
        );
        return res;
    } catch (error) {
        console.error("Error adding space members:", error);
        throw error;
    }
}

async function removeSpaceMember(spaceId: string, memberId: string) {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${spaceId}/members/${memberId}`,
            {
                method: "DELETE",
            }
        );
        return res;
    } catch (error) {
        console.error("Error removing space member:", error);
        throw error;
    }
}

async function addSpaceGuests(spaceId: string, data: AddSpaceGuestsPayload) {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${spaceId}/guests`,
            {
                method: "POST",
                body: JSON.stringify(data),
            }
        );
        return res;
    } catch (error) {
        console.error("Error adding space guests:", error);
        throw error;
    }
}

async function removeSpaceGuest(spaceId: string, guestId: string) {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${spaceId}/guests/${guestId}`,
            {
                method: "DELETE",
            }
        );
        return res;
    } catch (error) {
        console.error("Error removing space guest:", error);
        throw error;
    }
}

async function spaceGuestAccept(data: SpaceGuestAcceptPayload) {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/guests/invites/accept`,
            {
                method: "POST",
                body: JSON.stringify(data),
            }
        );
        return res;
    } catch (error) {
        console.error("Error accepting space guest invite:", error);
        throw error;
    }
}

async function spaceGuestLogin(data: SpaceGuestLoginPayload) {
    try {
        const res = await authService.apiGate(`${config.API_URL}/api/v1/ldb/spaces/guests/login`, {
            method: "POST",
            body: JSON.stringify(data),
        });
        return res;
    } catch (error) {
        console.error("Error logging in space guest:", error);
        throw error;
    }
}

async function spaceGuestTokenVerify(data: SpaceGuestTokenVerifyPayload) {
    try {
        const res = await authService.apiGate(`${config.API_URL}/api/v1/ldb/spaces/guests/token`, {
            method: "POST",
            body: JSON.stringify(data),
        });
        return res;
    } catch (error) {
        console.error("Error verifying space guest token:", error);
        throw error;
    }
}

async function getSpaceGuestInvitePreview(token: string) {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/guests/invites/preview/${token}`,
            {
                method: "GET",
            }
        );
        return res;
    } catch (error) {
        console.error("Error fetching space guest invite preview:", error);
        throw error;
    }
}

// Space Message Services
async function getSpaceMessages(spaceId: string, options: { page?: number; limit?: number } = {}) {
    try {
        const { page = 1, limit = 50 } = options; // Use 50 as default to match hook

        const queryParams = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
            sortBy: "createdAt",
            sortDirection: "desc", // Get newest first for proper pagination
        });

        const apiUrl = `${
            config.API_URL
        }/api/v1/ldb/spaces/${spaceId}/chat?${queryParams.toString()}`;

        const res = await authService.apiGate(apiUrl, {
            method: "GET",
        });

        return res;
    } catch (error) {
        console.error("Error fetching space messages:", error);
        throw error;
    }
}

async function sendSpaceMessage(spaceId: string, data: any) {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${spaceId}/chat`,
            {
                method: "POST",
                body: JSON.stringify(data),
            }
        );
        return res;
    } catch (error) {
        console.error("Error sending space message:", error);
        throw error;
    }
}

async function pinSpaceMessage(spaceId: string, messageId: string) {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${spaceId}/chat/${messageId}/pin`,
            {
                method: "PUT",
            }
        );
        return res;
    } catch (error) {
        console.error("Error pinning space message:", error);
        throw error;
    }
}

async function unpinSpaceMessage(spaceId: string, messageId: string) {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${spaceId}/chat/${messageId}/unpin`,
            {
                method: "PUT",
            }
        );
        return res;
    } catch (error) {
        console.error("Error unpinning space message:", error);
        throw error;
    }
}

async function editSpaceMessage(spaceId: string, messageId: string, data: any) {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${spaceId}/chat/${messageId}`,
            {
                method: "PUT",
                body: JSON.stringify(data),
            }
        );
        return res;
    } catch (error) {
        console.error("Error editing space message:", error);
        throw error;
    }
}

async function deleteSpaceMessage(spaceId: string, messageId: string) {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${spaceId}/chat/${messageId}`,
            {
                method: "DELETE",
            }
        );
        return res;
    } catch (error) {
        console.error("Error deleting space message:", error);
        throw error;
    }
}

async function getSpaceGuestBrands() {
    try {
        const res = await authService.apiGate(`${config.API_URL}/api/v1/ldb/spaces/guests/brands`, {
            method: "GET",
        });
        return res;
    } catch (error) {
        console.error("Error fetching space guest brands:", error);
        throw error;
    }
}

async function createSpaceFile(id: string, data: Record<string, any>) {
    const requestOptions = {
        method: "POST",
        body: JSON.stringify(data),
    };
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${id}/files`,
            requestOptions
        );
        return res;
    } catch (error) {
        console.error("Error fetching space details:", error);
        throw error;
    }
}

async function getSpaceFiles(
    id: string,
    page: number,
    limit: number,
    search?: string,
    fileTypes?: string,
    memberId?: string
) {
    const requestOptions = {
        method: "GET",
    };
    const urlParams = createUrlSearchParams({
        limit: limit as unknown as string,
        search: search as string,
        fileTypes: fileTypes as string,
        memberId: memberId as string,
    });

    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${id}/files?page=${page}&${urlParams.toString()}`,
            requestOptions
        );
        return res;
    } catch (error) {
        console.error("Error fetching space details:", error);
        throw error;
    }
}

async function getSingleSpaceFile(id: string, fileId: string) {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${id}/files/${fileId}`,
            {
                method: "GET",
            }
        );
        return res;
    } catch (error) {
        console.error("Error fetching space details:", error);
        throw error;
    }
}

async function deleteSpaceFile(id: string, fileId: string) {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${id}/files/${fileId}`,
            {
                method: "DELETE",
            }
        );
        return res;
    } catch (error) {
        console.error("Error fetching space details:", error);
        throw error;
    }
}

async function createSpaceNote(id: string, data: Record<string, any>) {
    try {
        const res = await authService.apiGate(`${config.API_URL}/api/v1/ldb/spaces/${id}/notes`, {
            method: "POST",
            body: JSON.stringify(data),
        });
        return res;
    } catch (error) {
        console.error("Error fetching space details:", error);
        throw error;
    }
}

async function updateSpaceNote(id: string, noteId: string, data: Record<string, any>) {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${id}/notes/${noteId}`,
            {
                method: "PUT",
                body: JSON.stringify(data),
            }
        );
        return res;
    } catch (error) {
        console.error("Error fetching space details:", error);
        throw error;
    }
}

async function getSpaceNotes(
    id: string,
    page: number,
    limit: number,
    search?: string,
    createdBy?: string,
    sortBy?: string
) {
    const urlParams = createUrlSearchParams({
        limit: limit as unknown as string,
        search: search as string,
        createdBy: createdBy as string,
        sortBy: sortBy as string,
    });
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${id}/notes?page=${page}&${urlParams.toString()}`,
            {
                method: "GET",
            }
        );
        return res;
    } catch (error) {
        console.error("Error fetching space details:", error);
        throw error;
    }
}

async function getSpaceSingleNote(id: string, noteId: string) {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${id}/notes/${noteId}`,
            {
                method: "GET",
            }
        );
        return res;
    } catch (error) {
        console.error("Error fetching space details:", error);
        throw error;
    }
}
async function deleteSpaceNote(id: string, noteId: string) {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${id}/notes/${noteId}`,
            {
                method: "DELETE",
            }
        );
        return res;
    } catch (error) {
        console.error("Error fetching space details:", error);
        throw error;
    }
}

async function pinSpaceNote(id: string, noteId: string) {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${id}/notes/${noteId}/pin`,
            {
                method: "PUT",
            }
        );
        return res;
    } catch (error) {
        console.error("Error fetching space details:", error);
        throw error;
    }
}

async function unpinSpaceNote(id: string, noteId: string) {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${id}/notes/${noteId}/unpin`,
            {
                method: "PUT",
            }
        );
        return res;
    } catch (error) {
        console.error("Error fetching space details:", error);
        throw error;
    }
}

async function leaveSpace(spaceId: string) {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${spaceId}/leave`,
            {
                method: "PUT",
            }
        );
        return res;
    } catch (error) {
        console.error("Error leaving space:", error);
        throw error;
    }
}

// Space Task Services
async function getSpaceTasks(spaceId: string, filters?: SpaceTaskFilters) {
    try {
        const queryParams = createUrlSearchParams((filters as any) || {});

        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${spaceId}/tasks?${queryParams.toString()}`,
            {
                method: "GET",
            }
        );
        return res;
    } catch (error) {
        console.error("Error fetching space tasks:", error);
        throw error;
    }
}

async function getSingleSpaceTask(spaceId: string, taskId: string) {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${spaceId}/tasks/${taskId}`,
            {
                method: "GET",
            }
        );
        return res;
    } catch (error) {
        console.error("Error fetching single space task:", error);
        throw error;
    }
}

async function getSpaceTasksRuns(
    spaceId?: string,
    page?: number,
    limit?: number,
    parentTaskId?: string,
    status?: string,
    search?: string,
    taskType?: string
) {
    try {
        const queryParams = createUrlSearchParams({
            page: page,
            limit: limit,
            parentTaskId: parentTaskId,
            status: status,
            taskType: taskType,
            search,
        });

        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${spaceId}/tasks?${queryParams.toString()}`,
            {
                method: "GET",
            }
        );
        return res;
    } catch (error) {
        console.error("Error fetching space tasks:", error);
        throw error;
    }
}

async function createSpaceTask(spaceId: string, data: CreateSpaceTaskPayload) {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${spaceId}/tasks`,
            {
                method: "POST",
                body: JSON.stringify(data),
            }
        );
        return res;
    } catch (error) {
        console.error("Error creating space task:", error);
        throw error;
    }
}

async function updateSpaceTask(spaceId: string, taskId: string, data: UpdateSpaceTaskPayload) {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${spaceId}/tasks/${taskId}`,
            {
                method: "PUT",
                body: JSON.stringify(data),
            }
        );
        return res;
    } catch (error) {
        console.error("Error updating space task:", error);
        throw error;
    }
}

async function deleteSpaceTask(spaceId: string, taskId: string) {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/${spaceId}/tasks/${taskId}`,
            {
                method: "DELETE",
            }
        );
        return res;
    } catch (error) {
        console.error("Error deleting space task:", error);
        throw error;
    }
}

async function fetchSpaceUnreadCount() {
    try {
        const res = await authService.apiGate(
            `${config.API_URL}/api/v1/ldb/spaces/chat/unread-count`,
            {
                method: "GET",
            }
        );
        return res;
    } catch (error) {
        throw error;
    }
}
